import React from 'react'
import { debounce } from 'lodash'

import withLayout from '~/hoc/withLayout'
import { withNamespaces } from '>/i18n'

import Footer from '~/components/Footer/FooterGql'
import FaqBlock from '~/components/FaqBlock'
import FaqNav from '~/components/FaqNav'

import { viewportWidth, hasWindow } from '~/utils/helpers'
import config from '~/config'
import Modal from '~/components/Modal'
import { commonModals } from '~/components/Modal/modalsList'
import withFestivalContext from '../../../src/hoc/withFestivalContext'

const faqOffset = (viewportWidth, festivalData) => {
  let offset

  if (viewportWidth < 768) {
    offset = -festivalData.faq.offset.mobile
  } else if (viewportWidth >= 768 && viewportWidth < 992) {
    offset = -festivalData.faq.offset.tablet
  } else {
    offset = -festivalData.faq.offset.desktop
  }

  return offset
}

const getOpenQuestionIndex = (url) => {
  const anchor = url.split('#')[1]
  if (!anchor) return

  const categoryId = +anchor.split('-')[0]
  const queryId = +anchor.split('-')[1]
  return { categoryId, queryId }
}

@withLayout({
  namespaces: ['faq'],
  contentClassName: 'festival'
})
@withNamespaces(['faq-gw'])
@withFestivalContext
export default class Faq extends React.Component {
  state = {
    viewportWidth: viewportWidth(),
    visibleSectionsIds: [],
  }

  componentDidMount () {
    const allSections = document.querySelectorAll('.faq-block__section')

    this.allSectionsArr = Array.from(allSections)
    this.allSectionsIds = this.allSectionsArr.map(section => section.id)
    this.lastSectionIdArr = this.allSectionsIds.slice(-1)

    this.initIntersectionObserver()
    this.observeSections()

    hasWindow() && window.addEventListener('resize', this.handleWindowResize)
  }

  componentWillUnmount () {
    this.observer.disconnect()
    hasWindow() && window.removeEventListener('resize', this.handleWindowResize)
  }

  initIntersectionObserver = () => {
    const config = {
      rootMargin: `-${this.festivalData.faq.offset.intersection}px 0px 0px 0px`,
      threshold: [0, 0.9, 1]
    }

    this.observer = new IntersectionObserver(this.activateNavItem, config)
  }

  observeSections = () => this.allSectionsArr.forEach(section => { this.observer.observe(section) })

  updateIntersectionObserver = () => {
    this.observer.disconnect()

    this.initIntersectionObserver()
    this.observeSections()
  }

  activateNavItem = elements => {
    let visibleSections = [...this.state.visibleSectionsIds]

    elements.forEach(element => {
      const elementId = element.target.id

      if (elementId === 'footer' && element.intersectionRatio >= 0.85) {
        visibleSections = this.lastSectionIdArr
      } else if (element.isIntersecting && elementId !== 'footer') {
        visibleSections.push(elementId)
        visibleSections = Array.from(new Set(visibleSections)) // remove duplicates from array
        visibleSections.sort((a, b) => this.allSectionsIds.indexOf(a) - this.allSectionsIds.indexOf(b))
      } else {
        visibleSections = visibleSections.filter(visibleSection => elementId !== visibleSection)
      }
    })

    this.setState({ visibleSectionsIds: visibleSections })
  }

  handleWindowResize = debounce(() => {
    this.updateDimensions()
    this.updateIntersectionObserver()
  }, 100)

  updateDimensions = () => {
    this.setState({ viewportWidth: viewportWidth() })
  }

  get festivalData () {
    const { festivalName } = this.props
    return config(festivalName).festival
  }

  render () {
    const { router, t } = this.props
    const { visibleSectionsIds, viewportWidth } = this.state
    const faqObject = t('faq', { returnObjects: true })

    const { asPath } = router
    const openQuestionIndex = getOpenQuestionIndex(asPath)

    return <React.Fragment>
      <div className='faq'>
        <div className='faq__heading-container'>
          <h1 className='faq__heading container'>{t('common:faq_heading')}</h1>
        </div>
        <div className='faq__content container'>
          <FaqNav
            faqObject={faqObject}
            offset={faqOffset(viewportWidth, this.festivalData)}
            activeNavId={visibleSectionsIds[0]}
          />
          <FaqBlock
            faqObject={faqObject}
            openQuestionIndex={openQuestionIndex}
            t={t}
          />
        </div>
      </div>
      <Footer observer={this.observer} />
      <Modal components={commonModals} />
    </React.Fragment>
  }
}
